import { useMemo, useRef } from 'react'
import { StatefulImage } from '../../rapid-ui'
import { ImageCrop, resolveImageCropBox, useImageWithAspect } from '../../utilities'
import { useIsAiGeneratedImage } from '../ai-generated-label'
import { EmptyImageLabel, useIsImageSlotEmpty } from './empty-image-hint'
import { useImageCache } from './image-cache'

export { campaignImageCacheAtom, useImageCache, useImageCacheWriter } from './image-cache'

export type CampaignImageProps = {
	imageId: string
	className?: string
	color: string

	containerWidth?: number | null
	containerHeight?: number | null

	/** Applied only alongside `frameAspectRatio`; either one on its own is ignored. */
	crop?: ImageCrop
	frameAspectRatio?: number
}

export function CampaignImage(
	{ imageId, className, color, containerWidth, containerHeight, crop, frameAspectRatio }: CampaignImageProps,
) {
	const { getImage } = useImageCache()
	const isAiGenerated = useIsAiGeneratedImage(imageId)

	const { url: cachedUrl, isReady } = getImage(imageId)
	const isEmpty = useIsImageSlotEmpty(imageId)

	// A regeneration swaps the image id before the editor has cached anything
	// for it, so the last shown image stays up until the cache catches up.
	const lastShownUrl = useRef('')
	if (cachedUrl) {
		lastShownUrl.current = cachedUrl
	}
	const imageUrl = isEmpty ? '' : cachedUrl || lastShownUrl.current
	const isGenerating = !isEmpty && (!cachedUrl || !isReady)

	const { width: naturalWidth, height: naturalHeight } = useImageWithAspect(imageUrl)

	// Calculate cover dimensions and centering offset when container size is provided
	const coverDimensions = useMemo(() => {
		if (!containerWidth || !containerHeight || !naturalWidth || !naturalHeight) {
			return { width: undefined, height: undefined }
		}

		const scaleX = containerWidth / naturalWidth
		const scaleY = containerHeight / naturalHeight
		const scale = Math.max(scaleX, scaleY)

		const scaledWidth = naturalWidth * scale
		const scaledHeight = naturalHeight * scale

		return {
			width: scaledWidth,
			height: scaledHeight,
		}
	}, [containerWidth, containerHeight, naturalWidth, naturalHeight])

	// StatefulImage paints via background-image, not an <img>, so this has to be a
	// background-size/position pair.
	const cropStyle = useMemo(() => {
		if (!crop || !frameAspectRatio) { return undefined }

		const box = resolveImageCropBox(crop, naturalWidth ?? 0, naturalHeight ?? 0, frameAspectRatio)
		if (!box) { return undefined }

		return {
			backgroundSize: `${box.widthPercent}% ${box.heightPercent}%`,
			backgroundPosition: `${crop.imageOffsetX}% ${crop.imageOffsetY}%`,
		}
	}, [crop, frameAspectRatio, naturalWidth, naturalHeight])

	return (
		<StatefulImage
			src={imageUrl}
			className={className}
			isGenerating={isGenerating}
			emptyPlaceholder={<EmptyImageLabel />}
			data-ai-generated={isAiGenerated ? 'true' : undefined}
			style={{
				backgroundColor: color,
				width: coverDimensions.width ?? (isEmpty ? containerWidth : null) ?? '100%',
				height: coverDimensions.height ?? (isEmpty ? containerHeight : null) ?? '100%',
				...cropStyle,
			}}
		/>
	)
}
